import React from "react";
import Button from "react-bootstrap/Button";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import BaseHeader from "@/components/Common/BaseHeader";
import Introductions from "@/components/HeaderButtons/Introductions";
import Notifications from "@/components/HeaderButtons/Notifications";
import DeleteNotificationModalNew from "@/components/Onboarding/DeleteNotificationModalNew";
import WantAdRenewConfirmationModal from "@/components/Onboarding/WantAdRenewConfirmationModal";
import { SIGNUP, LOGIN, POST_WANT_AD } from "@/constants/onBoarding";
import { pageURLs } from "@/constants";
import Client from "@/images/v2/Client-icon.svg";
import Home from "@/images/v2/Home-icon.svg";
import Inventory from "@/images/v2/Inventory.svg";
import Wantlisting from "@/images/v2/Wantlisting.svg";
import BellIcon from "@/images/v2/Bell-icon.svg";
import HandshakeIcon from "@/images/v2/Handshake-icon.svg";
import { HeaderSidebarBaseWrapper } from "./HeaderSidebar";
import BlsSidebar from "@/components/BlsSidebar";
import MobileBottomNav from "./MobileBottomNav";
import { useUserDetails } from "@/context";
import { useMLSAgentAtGitchaRedirect } from "@/hooks/useMLSAgentAtGitchaRedirect";
import { useCopilotEnabled } from "@/hooks/useCopilotEnabled";
import CopilotIcon from "@/images/v2/genie-mark.svg";
import { getAppPathname } from "@/helpers/basePath";


export const Sidebar = (props) => <BlsSidebar {...props} />;

const HeaderSidebarAgent = React.forwardRef((allProps, headerRef) => {

  const {
    user,
    userDetails,
    userPlan,
    showAgentMenuNotificationIcon,
    currentURL,
    handleAgentOnboardingModal,
    PremiumPlanModalShow,
    handleLogOut,
    headerHeight,
    handleGoFeed,
    handleGoInventory,
    goTo,
    ContactModalShow,
    BasicPlanModalShow,
    shouldDisplayWantListingButton,
    introductionList,
    isLoadIntroList,
    unreadMessageCount,
    notificationList,
    isLoadNotificationList,
    unreadNotificationMessageCount,
    unreadInappNotificationCount,
    deleteNotificationModal,
    wantAdRenewModal,
    handleHandShakeClick,
    updateIntroStateonHover,
    updateIntroPageOnScroll,
    handleCloseIntroList,
    updateInAppStatusonHover,
    updateInAppDeleteStatusOnClick,
    renewWantAd,
    onDeleteInappNotification,
    onConfirmReniewWantAdClicked,
    handleDeleteNotificationPopupClose,
    handleWantAdRenewPopupClose,
    isGitchaDefaultWebsite,
  } = allProps;

  const navigate = useNavigate();

  const handlePostWantAdClick = () => {
    const hasUser = _.get(user, "userInfo", null);

    if (!hasUser) {
      return handleAgentOnboardingModal({
        showPopup: true,
        userAction: SIGNUP,
        userPreference: POST_WANT_AD,
      });
    }

    return navigate(pageURLs.agentCreatewantadMainScreen);
  };

  const handleGoClients = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (getAppPathname() !== pageURLs.agentMyClients) {
      goTo(pageURLs.agentMyClients);
    }
  };

  const handleGoWantListings = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (getAppPathname() !== pageURLs.agentMyWantAdListings) {
      goTo(pageURLs.agentMyWantAdListings);
    }
  };

  const { shouldToggleColor } = useUserDetails();

  const { redirectToBLSEnv }= useMLSAgentAtGitchaRedirect();

  const copilotEnabled = useCopilotEnabled();

  const handleGoChat = () => {
    const userInfo = _.get(user, "userInfo", null);
    if (!userInfo) {
      handleAgentOnboardingModal({ showPopup: true, userAction: SIGNUP });
      return;
    }
    if (getAppPathname() !== pageURLs.agentChat) {
      goTo(pageURLs.agentChat);
    }
  };

  const headerContent = HeaderSidebarBaseWrapper({
    ...allProps,
    headerExtraButtons: () => {

      if (!shouldDisplayWantListingButton) {
        return null;
      }

      return (
        <Button
          variant={shouldToggleColor ? "tertiary" : "primary"}
          className="ml-6 mr-2 btn-tweak"
          onClick={handlePostWantAdClick}
        >
          Post Want-Listing
        </Button>
      )
    },
    sidebar: <Sidebar
      currentURL={currentURL}
      handleGoInventory={handleGoInventory}
      handleGoFeed={handleGoFeed}
      handleGoClients={handleGoClients}
      handleGoWantListings={handleGoWantListings}
      handleGoChat={handleGoChat}
      shouldToggleColor={shouldToggleColor}
    />,
    mobileNav: <MobileBottomNav
      // Chat is a 3rd leftItem (not the "+" popup, which is reserved for
      // creation actions - Want-Listing/Inventory) - this mirrors Feed/My
      // Clients as a direct, always-visible entry point into the same
      // full-page ChatPage used on desktop (see App.js's pageURLs.agentChat
      // route). The page itself becomes a left-drawer layout on mobile
      // (see ChatPage.scss's history-panel drawer), not this nav.
      leftItems={[
        { icon: Home, label: "Feed", onClick: handleGoFeed, isActive: currentURL === "/" || currentURL === pageURLs.home },
        { icon: Client, label: "My Clients", onClick: () => redirectToBLSEnv({ path: pageURLs.agentMyClients, onGitchaEnvFallback: handleGoClients }), isActive: currentURL === pageURLs.agentMyClients },
        ...(copilotEnabled ? [{ icon: CopilotIcon, label: "Genie", onClick: handleGoChat, isActive: currentURL.startsWith(pageURLs.agentChat), tourId: "genie-ai" }] : []),
      ]}
      addMenuItems={[
        { icon: Wantlisting, label: "Want-Listing", onClick: handleGoWantListings, isActive: currentURL === pageURLs.agentMyWantAdListings },
        { icon: Inventory, label: "Inventory", onClick: () => redirectToBLSEnv({ path: pageURLs.agentInventoryListing, onGitchaEnvFallback: handleGoInventory }), isActive: currentURL === pageURLs.agentInventoryListing },
      ]}
      onAddClick={!_.get(user, "userInfo", null) ? () => handleAgentOnboardingModal({ showPopup: true, userAction: isGitchaDefaultWebsite ? SIGNUP : LOGIN }) : null}
      notificationConfig={_.get(user, "userInfo", null) ? {
        icon: BellIcon,
        totalBadge: (unreadMessageCount || 0) + (unreadNotificationMessageCount || 0) + (unreadInappNotificationCount || 0),
        items: [
          {
            icon: HandshakeIcon,
            label: "Introductions",
            panelKey: "introductions",
            badge: unreadMessageCount || 0,
            onOpen: handleHandShakeClick,
          },
          {
            icon: BellIcon,
            label: "Notifications",
            panelKey: "notifications",
            badge: unreadNotificationMessageCount || unreadInappNotificationCount || 0,
            onOpen: updateInAppStatusonHover,
          },
        ],
        panels: {
          introductions: (
            <Introductions
              list={introductionList}
              updateIntroStateonHover={updateIntroStateonHover}
              updateIntroPageOnScroll={updateIntroPageOnScroll}
              isLoadIntroList={isLoadIntroList}
              handleCloseIntroList={handleCloseIntroList}
            />
          ),
          notifications: (
            <div className="w-100 h-100 position-relative notif-modal-container">
              <Notifications
                list={notificationList}
                isLoadNotificationList={isLoadNotificationList}
                onDeleteInappNotification={() => onDeleteInappNotification()}
                updateInAppDeleteStatusOnClick={updateInAppDeleteStatusOnClick}
                renewWantAd={(notification) => renewWantAd(notification)}
              />
              <DeleteNotificationModalNew
                show={deleteNotificationModal}
                onHide={handleDeleteNotificationPopupClose}
                onConfirm={updateInAppDeleteStatusOnClick}
              />
              <WantAdRenewConfirmationModal
                show={wantAdRenewModal}
                onHide={handleWantAdRenewPopupClose}
                onConfirmReniewWantAd={onConfirmReniewWantAdClicked}
              />
            </div>
          ),
        },
      } : null}
    />,
    authButtonsProps: {
      signInText: "Login",
      handleAgentOnboardingModal: handleAgentOnboardingModal,
    },
    headerSidebarDropdownMenuProps: {
      user,
      userDetails,
      userPlan,
      showAgentMenuNotificationIcon,
      navigate,
      PremiumPlanModalShow,
      handleLogOut,
      ContactModalShow,
      BasicPlanModalShow
    },
  })

  return <BaseHeader {...allProps} headerContent={headerContent} />;

})

export default HeaderSidebarAgent;
